import { type FormEvent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { api } from '@/lib/api';
import { uploadFile } from '@/lib/attachment-upload';
import { Briefcase, Landmark, FolderKanban, Link2, Loader2, Paperclip, RefreshCw } from 'lucide-react';
import { useChatContextStore } from '@/stores/chat-context-store';
import { CeoCoCeoPanel } from '@/components/admin/CeoCoCeoPanel';

interface CeoDeskSummary {
  strategyStatus: Array<{ status: string; count: string }>;
  fundraisingStage: Array<{ stage: string; count: string }>;
  dataroomStatus: Array<{ status: string; count: string }>;
  latestStrategy: Array<Record<string, unknown>>;
  latestFundraising: Array<Record<string, unknown>>;
  latestDataroom: Array<Record<string, unknown>>;
}

interface CeoAttachment {
  id: string;
  doc_type: string | null;
  file_name: string;
  file_url: string;
  mime_type: string | null;
  created_at: string;
}

type StrategyForm = {
  pillar: string;
  itemTitle: string;
  status: string;
  priority: string;
  summary: string;
};

type FundraisingForm = {
  investorName: string;
  roundName: string;
  stage: string;
  status: string;
  nextStep: string;
  notes: string;
};

type DataRoomForm = {
  category: string;
  itemName: string;
  status: string;
  sourceUrl: string;
  notes: string;
};

type CeoDeskPanel = 'overview' | 'strategy' | 'fundraising' | 'dataroom' | 'co-ceo';

const CEO_PANEL_IDS: Record<Exclude<CeoDeskPanel, 'overview'>, string> = {
  strategy: 'ceo-strategy-section',
  fundraising: 'ceo-fundraising-section',
  dataroom: 'ceo-dataroom-section',
  'co-ceo': 'ceo-coco-section',
};

export default function CeoDeskPage() {
  const mergePageContext = useChatContextStore((state) => state.mergePageContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState<CeoDeskSummary | null>(null);
  const [strategyItems, setStrategyItems] = useState<Array<Record<string, unknown>>>([]);
  const [fundraisingItems, setFundraisingItems] = useState<Array<Record<string, unknown>>>([]);
  const [dataroomItems, setDataroomItems] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [strategyForm, setStrategyForm] = useState<StrategyForm>({ pillar: 'platform', itemTitle: '', status: 'planned', priority: 'high', summary: '' });
  const [fundraisingForm, setFundraisingForm] = useState<FundraisingForm>({ investorName: '', roundName: 'current_round', stage: 'target', status: 'active', nextStep: '', notes: '' });
  const [dataRoomForm, setDataRoomForm] = useState<DataRoomForm>({ category: 'general', itemName: '', status: 'missing', sourceUrl: '', notes: '' });
  const requestedPanel = searchParams.get('panel');
  const activePanel: CeoDeskPanel = requestedPanel === 'strategy'
    || requestedPanel === 'fundraising'
    || requestedPanel === 'dataroom'
    || requestedPanel === 'co-ceo'
    ? requestedPanel
    : 'overview';

  async function load() {
    setLoading(true);
    try {
      const [summaryData, strategyData, fundraisingData, dataroomData] = await Promise.all([
        apiFetch<CeoDeskSummary>('/ceo-desk/summary'),
        apiFetch<Array<Record<string, unknown>>>('/ceo-desk/strategy-items?limit=50'),
        apiFetch<Array<Record<string, unknown>>>('/ceo-desk/fundraising?limit=50'),
        apiFetch<Array<Record<string, unknown>>>('/ceo-desk/dataroom?limit=50'),
      ]);
      setSummary(summaryData);
      setStrategyItems(strategyData);
      setFundraisingItems(fundraisingData);
      setDataroomItems(dataroomData);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (loading || activePanel === 'overview') return;
    const targetId = CEO_PANEL_IDS[activePanel];
    const element = document.getElementById(targetId);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activePanel, loading]);

  useEffect(() => {
    mergePageContext({
      entityId: 'ceo_desk',
      entityType: 'ceo_desk',
      metadata: {
        entityLabel: 'CEO Desk',
        entityTitle: 'Strategy, fundraising and data-room control',
        strategyStatus: summary?.strategyStatus ?? [],
        fundraisingStage: summary?.fundraisingStage ?? [],
        dataroomStatus: summary?.dataroomStatus ?? [],
      },
      taskContext: {
        taskType: 'executive_control_plane',
        taskGoal: 'Drive strategy execution, fundraising progress, and investor-grade dataroom readiness.',
        currentStep: 'executive_review',
        completionCriteria: [
          'Strategic priorities are explicit',
          'Fundraising pipeline has next actions',
          'Dataroom evidence is investor-ready',
        ],
        recommendedNextActions: [
          'Review top strategy blockers',
          'Check fundraising next steps and stalled investors',
          'Close dataroom readiness gaps',
        ],
      },
      executionContext: {
        workspace: 'ceo_desk',
        strategyItemCount: summary?.latestStrategy?.length ?? 0,
        fundraisingItemCount: summary?.latestFundraising?.length ?? 0,
        dataroomItemCount: summary?.latestDataroom?.length ?? 0,
      },
      permissionContext: {
        canCreateExecutiveItems: true,
        canReviewFundraising: true,
        canReviewDataroom: true,
      },
      contextBudget: {
        objectScope: 'current_surface',
        threadItemsLimit: 12,
        riskLimit: 6,
        proofGapLimit: 6,
      },
    });

    return () => {
      mergePageContext({
        entityId: undefined,
        entityType: undefined,
        metadata: undefined,
        taskContext: undefined,
        executionContext: undefined,
        permissionContext: undefined,
        contextBudget: undefined,
      });
    };
  }, [mergePageContext, summary]);

  async function submitStrategy(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiFetch('/ceo-desk/strategy-items', {
        method: 'POST',
        body: JSON.stringify(strategyForm),
      });
      setStrategyForm({ pillar: 'platform', itemTitle: '', status: 'planned', priority: 'high', summary: '' });
      await load();
    } finally {
      setSubmitting(false);
    }
  }

  async function submitFundraising(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiFetch('/ceo-desk/fundraising', {
        method: 'POST',
        body: JSON.stringify(fundraisingForm),
      });
      setFundraisingForm({ investorName: '', roundName: 'current_round', stage: 'target', status: 'active', nextStep: '', notes: '' });
      await load();
    } finally {
      setSubmitting(false);
    }
  }

  async function submitDataroom(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiFetch('/ceo-desk/dataroom', {
        method: 'POST',
        body: JSON.stringify(dataRoomForm),
      });
      setDataRoomForm({ category: 'general', itemName: '', status: 'missing', sourceUrl: '', notes: '' });
      await load();
    } finally {
      setSubmitting(false);
    }
  }

  function switchPanel(panel: CeoDeskPanel) {
    const next = new URLSearchParams(searchParams);
    if (panel === 'overview') {
      next.delete('panel');
    } else {
      next.set('panel', panel);
    }
    setSearchParams(next, { replace: true });
  }

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-8 px-4 py-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium uppercase tracking-[0.24em] text-slate-500">
            <Briefcase className="h-3.5 w-3.5" />
            CEO Desk
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Strategy, fundraising and data-room control</h1>
          <p className="max-w-3xl text-sm leading-6 text-slate-600">
            把 CEO 的战略推进、融资跟进与 data-room 就绪度放进同一个内部执行台，而不是散在文档与聊天里。
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      <section className="flex flex-wrap gap-2">
        {[
          { id: 'overview', label: '概览' },
          { id: 'strategy', label: '战略' },
          { id: 'fundraising', label: '融资' },
          { id: 'dataroom', label: '资料室' },
          { id: 'co-ceo', label: 'AI Co-CEO' },
        ].map((panel) => {
          const isActive = activePanel === panel.id;
          return (
            <button
              key={panel.id}
              type="button"
              onClick={() => switchPanel(panel.id as CeoDeskPanel)}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                isActive
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
              }`}
            >
              {panel.label}
            </button>
          );
        })}
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-blue-50 p-5 text-blue-950">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] opacity-70"><Briefcase className="h-4 w-4" /> 战略</div>
          <div className="mt-3 text-sm leading-6">
            {(summary?.strategyStatus ?? []).map((row) => (
              <div key={row.status} className="flex items-center justify-between"><span>{row.status}</span><span>{row.count}</span></div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-emerald-50 p-5 text-emerald-950">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] opacity-70"><Landmark className="h-4 w-4" /> 融资</div>
          <div className="mt-3 text-sm leading-6">
            {(summary?.fundraisingStage ?? []).map((row) => (
              <div key={row.stage} className="flex items-center justify-between"><span>{row.stage}</span><span>{row.count}</span></div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-amber-50 p-5 text-amber-950">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.22em] opacity-70"><FolderKanban className="h-4 w-4" /> 资料室</div>
          <div className="mt-3 text-sm leading-6">
            {(summary?.dataroomStatus ?? []).map((row) => (
              <div key={row.status} className="flex items-center justify-between"><span>{row.status}</span><span>{row.count}</span></div>
            ))}
          </div>
        </div>
      </section>

      <div id="ceo-coco-section" className={activePanel === 'co-ceo' ? 'rounded-3xl ring-2 ring-slate-300 ring-offset-2' : ''}>
        <CeoCoCeoPanel />
      </div>

      <section className="grid gap-6 xl:grid-cols-3">
        <form id="ceo-strategy-section" onSubmit={(e) => void submitStrategy(e)} className={`rounded-3xl border bg-white p-6 ${activePanel === 'strategy' ? 'border-slate-900 ring-2 ring-slate-200' : 'border-slate-200'}`}>
          <h2 className="text-lg font-semibold text-slate-900">战略规划</h2>
          <div className="mt-4 space-y-3">
            <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={strategyForm.pillar} onChange={(e) => setStrategyForm((prev) => ({ ...prev, pillar: e.target.value }))}>
              {['platform', 'product', 'growth', 'finance', 'fundraising', 'operations'].map((pillar) => <option key={pillar} value={pillar}>{pillar}</option>)}
            </select>
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="战略事项" value={strategyForm.itemTitle} onChange={(e) => setStrategyForm((prev) => ({ ...prev, itemTitle: e.target.value }))} />
            <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={strategyForm.status} onChange={(e) => setStrategyForm((prev) => ({ ...prev, status: e.target.value }))}>
              {['planned', 'in_progress', 'watch', 'done', 'dropped'].map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
            <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={strategyForm.priority} onChange={(e) => setStrategyForm((prev) => ({ ...prev, priority: e.target.value }))}>
              {['low', 'normal', 'high', 'critical'].map((priority) => <option key={priority} value={priority}>{priority}</option>)}
            </select>
            <textarea className="min-h-[100px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="摘要" value={strategyForm.summary} onChange={(e) => setStrategyForm((prev) => ({ ...prev, summary: e.target.value }))} />
            <button disabled={submitting || !strategyForm.itemTitle.trim()} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">新增战略事项</button>
          </div>
          <div className="mt-5 space-y-3">
            {strategyItems.slice(0, 6).map((item) => (
              <div key={String(item.id)} className="rounded-2xl border border-slate-200 p-4">
                <div className="font-medium text-slate-900">{String(item.item_title)}</div>
                <div className="mt-1 text-sm text-slate-500">{String(item.pillar)} · {String(item.status)} · {String(item.priority)}</div>
                {item.summary ? <p className="mt-2 text-sm leading-6 text-slate-600">{String(item.summary)}</p> : null}
                <CeoDeskAttachmentPanel resource="strategy-items" itemId={String(item.id)} entityType="ceo_strategy_item" />
              </div>
            ))}
          </div>
        </form>

        <form id="ceo-fundraising-section" onSubmit={(e) => void submitFundraising(e)} className={`rounded-3xl border bg-white p-6 ${activePanel === 'fundraising' ? 'border-slate-900 ring-2 ring-slate-200' : 'border-slate-200'}`}>
          <h2 className="text-lg font-semibold text-slate-900">融资推进</h2>
          <div className="mt-4 space-y-3">
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="投资人" value={fundraisingForm.investorName} onChange={(e) => setFundraisingForm((prev) => ({ ...prev, investorName: e.target.value }))} />
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="轮次名称" value={fundraisingForm.roundName} onChange={(e) => setFundraisingForm((prev) => ({ ...prev, roundName: e.target.value }))} />
            <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={fundraisingForm.stage} onChange={(e) => setFundraisingForm((prev) => ({ ...prev, stage: e.target.value }))}>
              {['target', 'contacted', 'meeting', 'diligence', 'term_sheet', 'closed', 'passed'].map((stage) => <option key={stage} value={stage}>{stage}</option>)}
            </select>
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="下一步" value={fundraisingForm.nextStep} onChange={(e) => setFundraisingForm((prev) => ({ ...prev, nextStep: e.target.value }))} />
            <textarea className="min-h-[100px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="备注" value={fundraisingForm.notes} onChange={(e) => setFundraisingForm((prev) => ({ ...prev, notes: e.target.value }))} />
            <button disabled={submitting || !fundraisingForm.investorName.trim()} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">新增融资事项</button>
          </div>
          <div className="mt-5 space-y-3">
            {fundraisingItems.slice(0, 6).map((item) => (
              <div key={String(item.id)} className="rounded-2xl border border-slate-200 p-4">
                <div className="font-medium text-slate-900">{String(item.investor_name)}</div>
                <div className="mt-1 text-sm text-slate-500">{String(item.round_name)} · {String(item.stage)} · {String(item.status)}</div>
                {item.next_step ? <p className="mt-2 text-sm leading-6 text-slate-600">下一步：{String(item.next_step)}</p> : null}
                <CeoDeskAttachmentPanel resource="fundraising" itemId={String(item.id)} entityType="ceo_fundraising_item" />
              </div>
            ))}
          </div>
        </form>

        <form id="ceo-dataroom-section" onSubmit={(e) => void submitDataroom(e)} className={`rounded-3xl border bg-white p-6 ${activePanel === 'dataroom' ? 'border-slate-900 ring-2 ring-slate-200' : 'border-slate-200'}`}>
          <h2 className="text-lg font-semibold text-slate-900">资料室</h2>
          <div className="mt-4 space-y-3">
            <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={dataRoomForm.category} onChange={(e) => setDataRoomForm((prev) => ({ ...prev, category: e.target.value }))}>
              {['corporate', 'product', 'financial', 'commercial', 'legal', 'operations', 'general'].map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="资料项名称" value={dataRoomForm.itemName} onChange={(e) => setDataRoomForm((prev) => ({ ...prev, itemName: e.target.value }))} />
            <select className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" value={dataRoomForm.status} onChange={(e) => setDataRoomForm((prev) => ({ ...prev, status: e.target.value }))}>
              {['missing', 'draft', 'ready', 'review_needed', 'published'].map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
            <input className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="来源 URL" value={dataRoomForm.sourceUrl} onChange={(e) => setDataRoomForm((prev) => ({ ...prev, sourceUrl: e.target.value }))} />
            <textarea className="min-h-[100px] w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="备注" value={dataRoomForm.notes} onChange={(e) => setDataRoomForm((prev) => ({ ...prev, notes: e.target.value }))} />
            <button disabled={submitting || !dataRoomForm.itemName.trim()} className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">新增资料项</button>
          </div>
          <div className="mt-5 space-y-3">
            {dataroomItems.slice(0, 6).map((item) => (
              <div key={String(item.id)} className="rounded-2xl border border-slate-200 p-4">
                <div className="font-medium text-slate-900">{String(item.item_name)}</div>
                <div className="mt-1 text-sm text-slate-500">{String(item.category)} · {String(item.status)}</div>
                {item.source_url ? <p className="mt-2 truncate text-sm leading-6 text-slate-600">{String(item.source_url)}</p> : null}
                <CeoDeskAttachmentPanel resource="dataroom" itemId={String(item.id)} entityType="ceo_dataroom_item" />
              </div>
            ))}
          </div>
        </form>
      </section>
    </div>
  );
}

function CeoDeskAttachmentPanel({
  resource,
  itemId,
  entityType,
}: {
  resource: 'strategy-items' | 'fundraising' | 'dataroom';
  itemId: string;
  entityType: 'ceo_strategy_item' | 'ceo_fundraising_item' | 'ceo_dataroom_item';
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState<CeoAttachment[]>([]);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [form, setForm] = useState({
    docType: 'evidence',
    fileName: '',
    fileUrl: '',
    mimeType: '',
  });

  async function loadAttachments() {
    setLoading(true);
    try {
      const rows = await apiFetch<CeoAttachment[]>(`/ceo-desk/${resource}/${encodeURIComponent(itemId)}/attachments`);
      setAttachments(rows);
    } finally {
      setLoading(false);
    }
  }

  async function toggleOpen() {
    const next = !open;
    setOpen(next);
    if (next && attachments.length === 0) {
      await loadAttachments();
    }
  }

  async function submitAttachment() {
    setSaving(true);
    try {
      let payload = {
        docType: form.docType || undefined,
        fileName: form.fileName,
        fileUrl: form.fileUrl,
        mimeType: form.mimeType || undefined,
        metadata: undefined as Record<string, unknown> | undefined,
      };

      if (pendingFile) {
        const uploaded = await uploadFile(pendingFile, entityType, itemId);
        payload = {
          docType: form.docType || undefined,
          fileName: uploaded.fileName,
          fileUrl: uploaded.fileUrl,
          mimeType: uploaded.mimeType || undefined,
          metadata: uploaded.fileId ? { fileShareFileId: uploaded.fileId } : undefined,
        };
      }

      await api.post(`/ceo-desk/${resource}/${encodeURIComponent(itemId)}/attachments`, {
        docType: payload.docType,
        fileName: payload.fileName,
        fileUrl: payload.fileUrl,
        mimeType: payload.mimeType,
        metadata: payload.metadata,
      });

      setForm({ docType: 'evidence', fileName: '', fileUrl: '', mimeType: '' });
      setPendingFile(null);
      await loadAttachments();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4">
      <button
        type="button"
        onClick={() => void toggleOpen()}
        className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900"
      >
        <Paperclip className="h-4 w-4" />
        {open ? 'Hide attachments' : 'Manage attachments'}
      </button>

      {open ? (
        <div className="mt-4 space-y-4">
          <div className="space-y-3">
            <input
              type="file"
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setPendingFile(file);
                if (file) {
                  setForm((prev) => ({
                    ...prev,
                    fileName: file.name,
                    mimeType: file.type || prev.mimeType,
                  }));
                }
              }}
            />
            <input
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              placeholder="File name"
              value={form.fileName}
              onChange={(e) => setForm((prev) => ({ ...prev, fileName: e.target.value }))}
            />
            <input
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
              placeholder="File URL (optional if uploading file)"
              value={form.fileUrl}
              onChange={(e) => setForm((prev) => ({ ...prev, fileUrl: e.target.value }))}
            />
            <div className="grid gap-3 md:grid-cols-2">
              <input
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                placeholder="Doc type"
                value={form.docType}
                onChange={(e) => setForm((prev) => ({ ...prev, docType: e.target.value }))}
              />
              <input
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                placeholder="MIME type"
                value={form.mimeType}
                onChange={(e) => setForm((prev) => ({ ...prev, mimeType: e.target.value }))}
              />
            </div>
            <button
              type="button"
              onClick={() => void submitAttachment()}
              disabled={saving || !form.fileName.trim() || (!pendingFile && !form.fileUrl.trim())}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              <Link2 className="h-4 w-4" />
              {pendingFile ? 'Upload attachment' : 'Add attachment'}
            </button>
          </div>

          <div className="space-y-3">
            {loading ? (
              <div className="text-sm text-slate-500">Loading attachments...</div>
            ) : attachments.length === 0 ? (
              <div className="text-sm text-slate-500">No attachments yet.</div>
            ) : attachments.map((attachment) => (
              <a
                key={attachment.id}
                href={attachment.file_url}
                target="_blank"
                rel="noreferrer"
                className="block rounded-2xl border border-slate-200 bg-white p-3 transition hover:border-slate-300 hover:bg-slate-50"
              >
                <div className="font-medium text-slate-900">{attachment.file_name}</div>
                <div className="mt-1 text-sm text-slate-500">{attachment.doc_type ?? 'attachment'} · {attachment.mime_type ?? 'unknown'}</div>
                <div className="mt-2 truncate text-xs text-slate-400">{attachment.file_url}</div>
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
